/**
 * AINA embedded widget → host `onEvent` payloads (parity with Android `handleAinaFlow`).
 */

export const AINA_FLOW_OPEN_DOUBTS_SECTION = "OPEN_DOUBTS_SECTION";
export const AINA_FLOW_OPEN_MIC_INPUT = "OPEN_MIC_INPUT";
export const AINA_FLOW_OPEN_CAMERA_DOUBT = "OPEN_CAMERA_DOUBT";
/** Lowercase token — Kotlin branch `mAction == "doubts"` → `ainaStart()`. */
export const AINA_FLOW_DOUBTS = "doubts";

export function normalizeAinaWidgetEventPayload(event: unknown): unknown {
  if (typeof event === "string") {
    try {
      return JSON.parse(event) as unknown;
    } catch {
      return event;
    }
  }
  return event;
}

export function stripAinaFlowQuotes(s: string): string {
  let t = s.trim();
  if (t.length >= 2) {
    const a = t[0];
    const b = t[t.length - 1];
    if ((a === '"' && b === '"') || (a === "'" && b === "'")) {
      t = t.slice(1, -1);
    }
  }
  return t;
}

/**
 * Resolves a single action string from string / JSON / `{ type | action | eventType }` payloads
 * (Kotlin: `action.removeSurrounding("\"")` plus nested JSON).
 */
export function extractAinaFlowAction(event: unknown): string | null {
  let cur: unknown = event;
  for (let depth = 0; depth < 8; depth++) {
    cur = normalizeAinaWidgetEventPayload(cur);
    if (typeof cur === "string") {
      const stripped = stripAinaFlowQuotes(cur.trim());
      if (stripped !== cur.trim()) {
        cur = stripped;
        continue;
      }
      try {
        cur = JSON.parse(stripped) as unknown;
        continue;
      } catch {
        return stripped.length > 0 ? stripped : null;
      }
    }
    if (cur != null && typeof cur === "object" && !Array.isArray(cur)) {
      const o = cur as Record<string, unknown>;
      let next: string | undefined;
      for (const key of ["type", "action", "eventType", "event"] as const) {
        const v = o[key];
        if (typeof v === "string") {
          next = stripAinaFlowQuotes(v);
          break;
        }
      }
      if (next != null) {
        cur = next;
        continue;
      }
      return null;
    }
    return null;
  }
  return null;
}

export function isCameraDoubtsAinaEvent(event: unknown): boolean {
  if (event === AINA_FLOW_OPEN_CAMERA_DOUBT) {
    return true;
  }
  if (typeof event === "string") {
    const u = event.toUpperCase();
    if (u.includes("CAMERA") && u.includes("DOUBT")) {
      return true;
    }
  }
  if (event != null && typeof event === "object" && "type" in event) {
    const t = (event as { type: unknown }).type;
    if (t === AINA_FLOW_OPEN_CAMERA_DOUBT) {
      return true;
    }
    if (typeof t === "string") {
      const u = t.toUpperCase();
      if (u.includes("CAMERA") && u.includes("DOUBT")) {
        return true;
      }
    }
  }
  return false;
}

export type AinaHostFlowResolution =
  | { kind: "open_camera_doubt" }
  | { kind: "open_doubts_section"; noQuota: boolean }
  | { kind: "open_mic_input" }
  | { kind: "doubts_restart" }
  | { kind: "unknown" };

/**
 * Maps raw `onEvent` payload + current free-doubt count to host behavior
 * (Kotlin: `OPEN_DOUBTS_SECTION` → if count &lt;= 0 then `finishCFActivity` + `ainaStart()` else `checkCameraPermission()`;
 * `OPEN_MIC_INPUT` — host-specific; `doubts` → `ainaStart()`).
 */
export function resolveAinaHostFlow(
  event: unknown,
  noOfDoubts: number,
): AinaHostFlowResolution {
  const normalized = normalizeAinaWidgetEventPayload(event);
  if (isCameraDoubtsAinaEvent(normalized)) {
    return { kind: "open_camera_doubt" };
  }
  const mAction = extractAinaFlowAction(event);
  if (mAction == null) {
    return { kind: "unknown" };
  }
  if (mAction === AINA_FLOW_OPEN_DOUBTS_SECTION) {
    return { kind: "open_doubts_section", noQuota: noOfDoubts <= 0 };
  }
  if (mAction === AINA_FLOW_OPEN_MIC_INPUT) {
    return { kind: "open_mic_input" };
  }
  if (mAction === AINA_FLOW_DOUBTS) {
    return { kind: "doubts_restart" };
  }
  const u = mAction.toUpperCase();
  if (u.includes("OPEN_DOUBTS")) {
    return { kind: "open_doubts_section", noQuota: noOfDoubts <= 0 };
  }
  return { kind: "unknown" };
}
